import React, { useEffect, useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import { X, User, ArrowCounterClockwise, CircleNotch, Coins } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useWorkflowStore } from '@/stores/workflowStore'
import { usePlaygroundWalker } from './usePlaygroundWalker'
import type { GeneratedDeck } from './usePlaygroundWalker'
import { PlaygroundContactSelector } from './PlaygroundContactSelector'
import { PlaygroundTimeline } from './PlaygroundTimeline'
import { computeUpcomingNodes } from './upcomingSteps'
import { PlaygroundStepDetail } from './PlaygroundStepDetail'
import type { Workflow, ReplyScenario } from '@/types/workflow'
import { useScrollLock } from '@/lib/useScrollLock'

interface Props {
  isOpen: boolean
  onClose: () => void
  workflowId: number
  workflowName: string
}

/**
 * A re-route the user has asked for but not yet confirmed. Both kinds discard
 * every step after the one being viewed, so both are gated by `ConfirmDialog`
 * (§16 — the native browser confirm is banned) rather than applied immediately.
 */
type PendingReroute =
  | { kind: 'branch'; choice: string }
  | { kind: 'reply'; scenario: ReplyScenario | undefined }

export const WorkflowPlaygroundModal: React.FC<Props> = ({ isOpen, onClose, workflowId, workflowName }) => {
  const { fetchWorkflowById } = useWorkflowStore()
  const [workflow, setWorkflow] = useState<Workflow | null>(null)
  const [loadingWorkflow, setLoadingWorkflow] = useState(true)
  const [contactName, setContactName] = useState('')
  const [pendingReroute, setPendingReroute] = useState<PendingReroute | null>(null)

  const walker = usePlaygroundWalker(workflow)

  // Lock background scroll while the playground is open
  useScrollLock(isOpen)

  // Load full workflow on mount
  useEffect(() => {
    if (!isOpen) return
    setLoadingWorkflow(true)
    fetchWorkflowById(workflowId)
      .then(wf => {
        setWorkflow(wf)
        setLoadingWorkflow(false)
      })
      .catch(() => setLoadingWorkflow(false))
  }, [isOpen, workflowId])

  const handleSelectContact = (contactId: number, name: string) => {
    setContactName(name)
    walker.setContact(contactId)
    walker.startSimulation()
  }

  const handleReset = () => {
    walker.reset()
    setContactName('')
    setPendingReroute(null)
  }

  const handleClose = () => {
    walker.reset()
    setContactName('')
    setPendingReroute(null)
    onClose()
  }

  // Escape closes the playground. Guarded on `pendingReroute`: the stacked
  // ConfirmDialog owns its own dismissal and stops propagation at its own node,
  // but this listener is on `document` and would still fire for any Escape that
  // doesn't bubble through the dialog — one keypress must never cancel the
  // confirm *and* tear down the playground under it. Same pattern as
  // ContactDetailModal's nested-delete-confirm guard.
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pendingReroute) handleClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
     
  }, [isOpen, pendingReroute])

  const { steps, currentStepIndex, viewingStepIndex } = walker.state
  const viewedStep = steps[viewingStepIndex]
  const hasStarted = steps.length > 0
  const upcomingNodes = useMemo(
    () => (workflow ? computeUpcomingNodes(workflow.nodes, workflow.edges, steps.map(st => st.nodeId)) : []),
    [workflow, steps],
  )
  const isViewingPast = viewingStepIndex >= 0 && viewingStepIndex < currentStepIndex

  // Find the most recent completed generateDeck step BEFORE the viewed step
  // (to show as attachment on email steps).
  const attachedDeck = useMemo<GeneratedDeck | undefined>(() => {
    if (!viewedStep || viewedStep.nodeType !== 'sendEmail') return undefined
    for (let i = viewingStepIndex - 1; i >= 0; i--) {
      const s = steps[i]
      if (s.nodeType === 'generateDeck' && s.status === 'completed' && s.generatedDeck) {
        return s.generatedDeck
      }
    }
    return undefined
  }, [steps, viewingStepIndex, viewedStep])

  // Selecting a different branch/variant while reviewing a past step re-routes the
  // path, so we clear the later steps first (behind a confirm).
  const handleSelectBranch = (choice: string) => {
    if (isViewingPast) {
      if (choice === viewedStep.branchChoice) { walker.backToCurrent(); return }
      setPendingReroute({ kind: 'branch', choice })
      return
    }
    walker.selectBranch(choice)
  }

  // Changing the reply on a past sendEmail is a path change only when a downstream
  // follow-up depends on it; otherwise we just update it in place.
  const handleSetReplyScenario = (scenario: ReplyScenario | undefined) => {
    if (isViewingPast && viewedStep?.nodeType === 'sendEmail'
      && scenario !== viewedStep.replyScenario
      && walker.replyGatesDownstream(viewedStep.nodeId)) {
      setPendingReroute({ kind: 'reply', scenario })
      return
    }
    walker.setReplyScenario(scenario)
  }

  // The post-confirm half of both handlers above. The ordering is load-bearing:
  // truncateForward must land first, and the follow-up must run from a *later*
  // tick — usePlaygroundWalker.selectBranch reads state.viewingStepIndex rather
  // than currentStepIndex precisely because this setTimeout(…, 0) hands it a
  // closure captured before the truncation. Moving the work here (out of the
  // old synchronous native-confirm guard) keeps that shape intact: `walker` is
  // still the pre-truncation render's value when the timer fires.
  const confirmReroute = () => {
    const pending = pendingReroute
    setPendingReroute(null)
    if (!pending) return
    walker.truncateForward(viewingStepIndex)
    setTimeout(() => {
      if (pending.kind === 'branch') walker.selectBranch(pending.choice)
      else walker.setReplyScenario(pending.scenario)
    }, 0)
  }

  // Cumulative token/cost across all completed steps
  const cumulativeUsage = useMemo(() => {
    let totalTokens = 0
    let totalCost = 0
    for (const step of walker.state.steps) {
      if (step.usageLog) {
        for (const entry of step.usageLog) {
          totalTokens += entry.input_tokens + entry.output_tokens
          totalCost += entry.cost
        }
      }
    }
    return { totalTokens, totalCost }
  }, [walker.state.steps])

  // Closed: render nothing. This guard MUST stay below both useMemo calls above.
  // It previously sat before them, so the hook count changed with `isOpen` and
  // opening the playground threw "Rendered fewer hooks than expected". The
  // derivations it now follows are all safe while closed — `walker.state` always
  // exists, `steps[viewingStepIndex]` is simply undefined, and both memos guard
  // that case — and the handlers below are only invoked on interaction.
  if (!isOpen) return null

  return createPortal(
    // Static blur backdrop (design §29/§45) — not animated, so it can't flash
    // twice when React StrictMode remounts the portal in dev. Only the card animates.
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-5xl h-[85vh] max-h-[800px] flex flex-col bg-card border border-border/60 dark:border-white/[0.08] rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border/50 dark:border-white/[0.08] bg-card">
            <div className="flex items-center gap-4 min-w-0">
              <h1 className="text-lg font-emphasis text-foreground truncate">{workflowName}</h1>
              <span className="text-xs font-medium px-2 py-1 rounded-md bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] flex-shrink-0">
                Playground
              </span>
              {contactName && (
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground flex-shrink-0">
                  <User className="w-3.5 h-3.5" />
                  {contactName}
                </span>
              )}
              {cumulativeUsage.totalTokens > 0 && (
                <span className="flex items-center gap-1.5 text-xs font-mono px-2.5 py-1 rounded-full bg-muted/70 text-muted-foreground dark:bg-white/[0.06] flex-shrink-0 tabular-nums">
                  <Coins className="w-3 h-3" />
                  {cumulativeUsage.totalTokens.toLocaleString()} tokens | ${cumulativeUsage.totalCost.toFixed(4)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {hasStarted && (
                <Button variant="ghost" size="sm" onClick={handleReset} icon={<ArrowCounterClockwise className="w-4 h-4" />}>
                  Reset
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={handleClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-hidden">
            {loadingWorkflow ? (
              <div className="flex items-center justify-center h-full">
                <CircleNotch className="w-8 h-8 animate-spin text-[#5e6ad2] dark:text-[#a5b4fc]" />
              </div>
            ) : !workflow ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                Failed to load workflow.
              </div>
            ) : !hasStarted ? (
              /* Phase 1: Contact Selection */
              <PlaygroundContactSelector onSelect={handleSelectContact} />
            ) : (
              /* Phase 2: Two-panel layout */
              <div className="flex h-full">
                {/* Left: Timeline */}
                <div className="w-[300px] border-r border-border/50 dark:border-white/[0.08] overflow-y-auto bg-muted/30 dark:bg-white/[0.02]">
                  <div className="px-4 py-3 border-b border-border/50 dark:border-white/[0.06]">
                    <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.06em]">Steps</p>
                  </div>
                  <PlaygroundTimeline
                    steps={steps}
                    currentStepIndex={currentStepIndex}
                    viewingStepIndex={viewingStepIndex}
                    upcomingNodes={upcomingNodes}
                    onStepClick={walker.goToStep}
                  />
                </div>

                {/* Right: Step Detail */}
                <div className="flex-1 overflow-y-auto">
                  {viewedStep ? (
                    <PlaygroundStepDetail
                      step={viewedStep}
                      isGenerating={viewedStep.status === 'generating'}
                      onGenerate={walker.generateCurrentStep}
                      onAdvance={walker.advanceToNext}
                      onSelectBranch={handleSelectBranch}
                      onSetReplyScenario={handleSetReplyScenario}
                      onCompleteAndAdvance={walker.completeAndAdvance}
                      isComplete={walker.state.isComplete && !isViewingPast}
                      attachedDeck={attachedDeck}
                      onDownloadDeckPdf={() => walker.downloadDeckPdf(viewingStepIndex)}
                      onDownloadDeck={() => walker.downloadDeckPptx(viewingStepIndex)}
                      contactId={walker.state.contactId ?? undefined}
                      isViewingPast={isViewingPast}
                      onReRun={() => walker.reRunStep(viewingStepIndex)}
                      onBackToCurrent={walker.backToCurrent}
                    />
                  ) : walker.state.isComplete ? (
                    <PlaygroundStepDetail
                      step={walker.state.steps[walker.state.steps.length - 1]}
                      isGenerating={false}
                      onGenerate={() => {}}
                      onAdvance={() => {}}
                      onSelectBranch={() => {}}
                      onSetReplyScenario={() => {}}
                      onCompleteAndAdvance={() => {}}
                      isComplete={true}
                    />
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Re-route guard (§16). Destructive tone: the generated emails/decks on
            the later steps are real work, and it opens focused on Cancel (§57). */}
        <ConfirmDialog
          open={!!pendingReroute}
          tone="destructive"
          title="Clear the later steps?"
          description={
            pendingReroute?.kind === 'reply'
              ? 'Changing the reply re-routes the simulation from this step, discarding every step after it along with any email or deck generated there.'
              : 'Choosing a different branch re-routes the simulation from this step, discarding every step after it along with any email or deck generated there.'
          }
          confirmLabel="Clear and re-route"
          onConfirm={confirmReroute}
          onCancel={() => setPendingReroute(null)}
        />
    </div>,
    document.body,
  )
}
